import { RRule, type Frequency, type Options } from "rrule";

import {
  INTERVAL_OPTIONS,
  WEEKDAYS,
} from "@/components/MaintenanceStrategyInterval/maintenanceStrategyIntervalOptions";
import { getDateISO, getDateJS } from "@/utils/dates";
import { filterNotEmpty } from "@/utils/format";

/**
 * @description Returns RRule in readable string format
 * @param rule string
 */
export function readableRRule(rule?: string | null): string | null {
  // TODO(cpannwitz): i18n for RRule, see this for inspiration: https://github.com/jakubroztocil/rrule/issues/13
  if (!rule) return null;
  return RRule.fromString(rule).toText();
}

/**
 * @description Returns next RRule occasion in readable string format
 * @param rule string
 */
export function nextRRuleOccasion(rule?: string | null): string | null {
  if (!rule) return null;
  const next = RRule.fromString(rule).after(getDateJS());
  if (!next) return null;
  return getDateISO(next);
}

export function getRRuleDatesBetween(interval: string, startDate: string, endDate: string): string[] {
  return RRule.fromString(interval)
    .between(getDateJS(startDate), getDateJS(endDate))
    .map((date) => getDateISO(date));
}

export function parseRRuleString(rrule: string) {
  const parsedRRule = RRule.fromString(rrule);

  const { freq, interval, byweekday, dtstart } = parsedRRule.origOptions;
  const startDate = dtstart ? getDateISO(dtstart) : null;

  const intervalOption = INTERVAL_OPTIONS.find(
    ({ frequency }) => frequency.freq === freq && frequency.interval === interval,
  );
  const byWeekDays = byweekday
    ? Array.isArray(byweekday)
      ? byweekday.map((weekday) => WEEKDAYS.find((w) => w.weekday === weekday)).filter(filterNotEmpty)
      : [WEEKDAYS.find((w) => w.weekday === byweekday)].filter(filterNotEmpty)
    : [];

  return {
    intervalOption,
    byWeekDays,
    startDate,
  };
}

interface CreateRRuleStringProps {
  freq: Frequency;
  interval: number;
  byweekday?: Options["byweekday"];
  startDate?: string | null;
}
export function createRRuleString({ freq, interval, byweekday, startDate }: CreateRRuleStringProps) {
  const dtstart = startDate ? getDateJS(startDate) : null;
  return new RRule(
    {
      freq,
      interval,
      dtstart,
      byweekday,
      wkst: RRule.MO,
    },
    true,
  ).toString();
}
